import { useSnackbar } from 'providers/snackbar';
import { useCallback, useEffect, useState } from 'react';

const LOCAL_STORAGE_PREFIX = 'aktify_feature_';

/**
 * Hook to manage feature flags via localStorage with keyboard shortcut activation
 *
 * @param featureName The name of the feature flag
 * @param friendlyDescription Description of the feature that will be shown to the user once enabled/disabled
 * @param shortcutKeys Array of key codes that need to be pressed simultaneously to toggle the feature * @returns [isEnabled, setEnabled] - Current state and function to manually toggle
 */
export const useFeatureFlag = (
  featureName: string,
  friendlyDescription: string,
  shortcutKeys: string[] = [],
): [boolean, (enabled: boolean) => void] => {
  const { openSnackbar } = useSnackbar();
  const storageKey = `${LOCAL_STORAGE_PREFIX}${featureName}`;

  const getInitialState = (): boolean => {
    try {
      const storedValue = localStorage.getItem(storageKey);
      return storedValue === 'true';
    } catch (e) {
      return false;
    }
  };

  const [isEnabled, setIsEnabled] = useState<boolean>(getInitialState);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      try {
        localStorage.setItem(storageKey, enabled.toString());
        setIsEnabled(enabled);
      } catch (e) {
        console.error('Error accessing localStorage:', e);
      }
    },
    [storageKey],
  );

  useEffect(() => {
    if (!shortcutKeys.length) return;

    const currentlyPressed = new Set<string>();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.key) return;
      currentlyPressed.add(event.key.toLowerCase());

      const allKeysPressed = shortcutKeys.every((key) => currentlyPressed.has(key.toLowerCase()));

      if (allKeysPressed && currentlyPressed.size === shortcutKeys.length) {
        event.preventDefault();
        event.stopPropagation();
        const newValue = !isEnabled;
        setEnabled(newValue);
        openSnackbar({
          message: `"${friendlyDescription}" is now ${newValue ? 'enabled' : 'disabled'}.`,
          duration: 2000,
          severity: newValue ? 'success' : 'warning',
        });
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.key) return;
      currentlyPressed.delete(event.key.toLowerCase());
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [featureName, isEnabled, setEnabled, shortcutKeys]);

  return [isEnabled, setEnabled];
};
